import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Hexagon, Sun, Moon, LayoutDashboard, Wallet as WalletIcon,
  Send, ListOrdered, Settings, Palette, Building2, LogOut, Activity, Copy,
  Globe2, Shield, CalendarClock, ShieldAlert, ShieldCheck, FileText, Receipt,
  Bot, Sparkles, Server, Webhook, Plug, KeyRound,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";
import { aiApi } from "../lib/ledgerApi";

function NavItem({ to, icon: Icon, label, end, testid, badge }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-sm border transition-colors text-sm ${
          isActive
            ? "border-foreground bg-foreground text-background"
            : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
        }`
      }
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="flex-1">{label}</span>
      {badge ? (
        <span className="px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-600 dark:text-amber-400 text-[10px] font-mono">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, business, logout } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem("neopay-theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("neopay-theme", theme);
  }, [theme]);

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  const isSuperAdmin = user?.role === "super_admin";
  const vpa = business?.full_vpa;
  const [holdsCount, setHoldsCount] = useState(0);
  const [aiOffline, setAiOffline] = useState(false);
  const [forceOverrides, setForceOverrides] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState(0);
  useEffect(() => {
    if (!user || !business) return;
    let alive = true;
    const tick = async () => {
      try {
        const h = await aiApi.myHolds();
        if (alive) setHoldsCount((h || []).filter((x) => x.status === "pending").length);
      } catch {}
      try {
        const ap = await import("../lib/ledgerApi").then((m) => m.agentsApi.approvals());
        if (alive) setPendingApprovals((ap || []).length);
      } catch {}
      if (user?.role === "super_admin") {
        try {
          const s = await aiApi.status();
          if (alive) {
            setAiOffline(!s.available);
            setForceOverrides(s.fraud_force_decision || {});
          }
        } catch {}
      }
    };
    tick();
    const i = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(i); };
  }, [user, business]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background text-foreground">
      <Toaster theme={theme} position="bottom-right" toastOptions={{ className: "font-mono text-xs" }} />

      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-border bg-card md:sticky md:top-0 md:self-start">
        <div className="px-4 py-4 border-b border-border flex items-center gap-2">
          <Link to="/app/dashboard" className="flex items-center gap-2 font-medium tracking-tight">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.product_name} className="w-7 h-7 object-contain rounded-sm border border-foreground/40" />
            ) : (
              <span className="w-7 h-7 inline-flex items-center justify-center border border-foreground/40 rounded-sm">
                <Hexagon className="w-4 h-4" strokeWidth={1.5} />
              </span>
            )}
            <span>{branding.product_name}</span>
          </Link>
        </div>

        {business && (
          <div className="px-4 py-4 border-b border-border">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Business</div>
            <div className="text-sm font-medium truncate">{business.legal_name}</div>
            <button
              data-testid="copy-vpa"
              onClick={() => {
                navigator.clipboard.writeText(vpa);
                toast.success("VPA copied");
              }}
              className="mt-1 flex items-center gap-1 text-[11px] font-mono text-brand hover:underline"
            >
              <span className="truncate">{vpa}</span>
              <Copy className="w-3 h-3 shrink-0" strokeWidth={1.5} />
            </button>
          </div>
        )}

        <nav className="px-3 py-3 space-y-1">
          <NavItem to="/app/dashboard" icon={LayoutDashboard} label="Dashboard" testid="appnav-dashboard" />
          <NavItem to="/app/wallets" icon={WalletIcon} label="Wallets" testid="appnav-wallets" />
          <NavItem to="/app/send" icon={Send} label="Send" testid="appnav-send" />
          <NavItem to="/app/transactions" icon={ListOrdered} label="Transactions" testid="appnav-transactions" />
          <NavItem to="/app/contracts" icon={FileText} label="Contracts" testid="appnav-contracts" />
          <NavItem to="/app/invoices" icon={Receipt} label="Invoices" testid="appnav-invoices" />
          <NavItem to="/app/holds" icon={Shield} label="Holds" testid="appnav-holds" badge={holdsCount > 0 ? holdsCount : null} />
          <NavItem to="/app/scheduled" icon={CalendarClock} label="Scheduled" testid="appnav-scheduled" />
          <NavItem to="/app/fx" icon={Globe2} label="FX rates" testid="appnav-fx" />
          <NavItem to="/app/developer" icon={Bot} label="Agents" testid="appnav-developer" />
          <NavItem to="/app/agent-approvals" icon={ShieldCheck} label="Agent approvals" testid="appnav-agent-approvals" badge={pendingApprovals > 0 ? pendingApprovals : null} />
          <NavItem to="/app/demo/agents" icon={Sparkles} label="Live demo" testid="appnav-demo-agents" />
          <NavItem to="/app/webhooks" icon={Webhook} label="Webhooks" testid="appnav-webhooks" />
          <NavItem to="/app/integrations/odoo" icon={Plug} label="Odoo" testid="appnav-odoo" />
          <NavItem to="/app/settings/api-keys" icon={KeyRound} label="API keys" testid="appnav-api-keys" />
          <NavItem to="/app/settings" icon={Settings} label="Settings" testid="appnav-settings" />
          {isSuperAdmin && (
            <>
              <div className="pt-4 pb-1 px-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Super-admin</div>
              <NavItem to="/app/admin/branding" icon={Palette} label="Branding" testid="appnav-admin-branding" />
              <NavItem to="/app/admin/businesses" icon={Building2} label="Businesses" testid="appnav-admin-businesses" />
              <NavItem to="/app/admin/fx" icon={Globe2} label="FX controls" testid="appnav-admin-fx" />
              <NavItem to="/app/admin/risk" icon={ShieldAlert} label="Risk audit" testid="appnav-admin-risk" />
              <NavItem to="/app/admin/compliance" icon={ShieldCheck} label="Compliance" testid="appnav-admin-compliance" />
              <NavItem to="/app/admin/validators" icon={Server} label="Validators" testid="appnav-admin-validators" />
              <NavItem to="/app/admin/network-health" icon={Activity} label="Network health" testid="appnav-admin-network-health" />
              <NavItem to="/app/admin/webhooks" icon={Webhook} label="Webhooks fleet" testid="appnav-admin-webhooks" />
            </>
          )}
          <div className="pt-4 pb-1 px-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Public</div>
          <NavItem to="/" icon={Activity} label="Block explorer" testid="appnav-explorer" end />
        </nav>

        <div className="px-3 mt-auto md:absolute md:bottom-0 md:left-0 md:right-0 py-4 border-t border-border bg-card">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs truncate">{user?.email}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{user?.role}</div>
            </div>
            <button
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              data-testid="app-theme-toggle"
              className="w-8 h-8 inline-flex items-center justify-center border border-border rounded-sm hover:border-foreground transition-colors"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            </button>
            <button
              onClick={onLogout}
              data-testid="app-logout"
              className="w-8 h-8 inline-flex items-center justify-center border border-border rounded-sm hover:border-status-tampered hover:text-status-tampered transition-colors"
              title="Log out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      <main key={location.pathname} className="flex-1 animate-fade-in-up min-w-0">
        {isSuperAdmin && aiOffline && (
          <div data-testid="ai-offline-banner" className="px-6 py-2 border-b border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            AI offline — rule-based decisions active.
            <a href="/app/admin/risk" className="ml-2 underline">Review controls →</a>
          </div>
        )}
        {isSuperAdmin && Object.keys(forceOverrides).length > 0 && (
          <div data-testid="force-decision-banner" className="px-6 py-2 border-b border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs flex flex-wrap items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="uppercase tracking-widest text-[10px]">DEV override</span>
            <span>Fraud engine forcing</span>
            {Object.entries(forceOverrides).map(([k, v]) => (
              <span key={k} className="px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/20 rounded-sm font-mono text-[10px]">
                {k} → {v}
              </span>
            ))}
            <a href="/app/admin/risk" className="ml-2 underline">Manage →</a>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
